import React from "react"
import HeroSection from "../../Shared/HeroSection/HeroSection"
import { graphql, useStaticQuery } from "gatsby"
import Container from "../../Shared/Container/Container"
import Section from "../../Shared/Section/Section"
import Cards from "../../Shared/Cards/Cards"
import styles from "./Recognise.module.css"
import ArticleRow from "../../Shared/ArticleRow/ArticleRow"
import HowDoesItWorkRow from "./HowDoesItWork/HowDoesItWorkRow"
import FixedFooter from "../../Shared/FixedFooter/FixedFooter"
import { routes } from "../../../config"

const Recognise = () => {
  const data = useStaticQuery(graphql`
    query recogniseQuery {
      heroImg: file(relativePath: { eq: "recognise/hero.jpg" }) {
        childImageSharp {
          fluid(quality: 100) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      sectionImg1: file(relativePath: { eq: "recognise/section1.jpg" }) {
        childImageSharp {
          fluid(quality: 100) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      keyBenefitsImg1: file(relativePath: { eq: "recognise/key-benefits-1.jpg" }) {
        childImageSharp {
          fluid(quality: 100) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      keyBenefitsImg2: file(relativePath: { eq: "recognise/key-benefits-2.jpg" }) {
        childImageSharp {
          fluid(quality: 100) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      keyBenefitsImg3: file(relativePath: { eq: "recognise/key-benefits-3.jpg" }) {
        childImageSharp {
          fluid(quality: 100) {
            ...GatsbyImageSharpFluid
          }
        }
      }
    }
  `)
  return (
    <div className={styles.reward}>
      <HeroSection
        preHeading={"RECOGNISE"}
        heading={"Recognition rich workplaces"}
        content={<> Let peer-to-peer recognition and feedback<br/> flourish across your team</>}
        imageCards={[
          { alt: "Recognition rich workplaces", src: data.heroImg.childImageSharp.fluid },
        ]}
      />
      <FixedFooter type={"how-it-works"}/>
      <Container>
        <Section heading={"Purposeful peer-to-peer recognition"}>
          <ArticleRow
            imageAlt={"Build stronger teams"}
            isReversed
            imageSrc={data.sectionImg1.childImageSharp.fluid}
            rowContent={
              <>
                <p>Positive recognition is something everyone needs. Whether you talk to a psychologists or a leading HR
                  consultancy it’s No. 1 in driving happy and engaged people.</p>
                <p>But the use of extrinsic rewards peer to peer can create environments that lack a key element of what it is to be human. This is no new idea either, the concept of ’crowding out’ has been around for decades.</p>
                <p>And that’s where a Cultural Currency comes in. Providing an individual the ability to make a positive
                  impact on others collectively creates something altogether different.</p>
                <p>Firstly <strong>co-creation</strong>, individuals get to see collectively what impact they’ve had.
                  Secondly <strong>pride in
                    their team</strong>, no-one tells their mates how proud they are that their team gave them a coffee
                  voucher.
                  Cultural Currencies however, now your talking.</p>
              </>
            }
          />
        </Section>
        <Section heading={"Key Benefits"}>
          <div className={styles.articleRows}>
            <ArticleRow
              imageAlt={"Purpose led workforces"}
              imageMaxWidth={461}
              imageSrc={data.keyBenefitsImg1.childImageSharp.fluid}
              rowContent={
                <>
                  <h3>Build stronger teams</h3>
                  <p>
                    Create cohesive teams where everyone feels appreciated, no matter their level as recognition is
                    driven peer-to-peer across all employees no matter their level
                  </p>
                </>
              }
            />
            <ArticleRow
              isReversed
              imageAlt={"Boost engagement"}
              imageMaxWidth={462}
              imageSrc={data.keyBenefitsImg2.childImageSharp.fluid}
              rowContent={
                <>
                  <h3>Boost engagement</h3>
                  <p>
                    Encourage frequent and timely recognition and feedback through integration with your communication
                    platform
                  </p>
                </>
              }
            />
            <ArticleRow
              imageAlt={"Purpose led workforces"}
              imageMaxWidth={462}
              imageSrc={data.keyBenefitsImg3.childImageSharp.fluid}
              rowContent={
                <>
                  <h3>Purpose led workforces</h3>
                  <p>
                    Connect social recognition to positive impact with your firms own Cultural Currency. Creating
                    employee led philanthropy and impactful actions. What’s more the benefits are seen by all instead of
                    an Amazon Voucher.
                  </p>
                </>
              }
            />
          </div>
        </Section>
        <Section heading={"How does it work"}>
          <HowDoesItWorkRow cards={[{
            header: "Receive your cultural currency",
            content: "Individuals receive an amount of your companies Cultural Currency. The amount they receive varies based on actions completed and/or how many people they’ve recognised with Cultural Currency previously.",
            icon: "currency",
          }, {
            header: "React to employee posts",
            content: "Every time an individual ‘likes’ an individuals message on a public or private chat they immediately give one cultural currency. Depending on the emoji used they can also give up to 100 to the recipient.",
            icon: "react",
          }]}/>
          <HowDoesItWorkRow cards={[{
            header: "Shout-outs receive cultural currency",
            content: "Individuals can also react to shout-outs to teammates by other teammates with Cultural Currency going to those mentioned in the shout-out. Thereby increasing the number of shout-outs happening across a team.",
            icon: "shout",
          }, {
            header: "Private recognition",
            content: "Individuals can use simple commands to give Cultural Currency with personalised messages and hashtags to individuals. Creating a great feedback loop through peer-to-peer recognition.",
            icon: "private",
          }]}/>
          <HowDoesItWorkRow cards={[{
            header: "Donate your cultural currency to innovative non-profits",
            content: "Individuals can choose from the most innovative and impactful charities in the world to donate to. Then see collectively there impact across there organisation.",
            icon: "donate",
          }, {
            header: "Try for 60 days FREE",
            content: <p>Get your Cultural Currency available on Slack today by clicking <a href={routes.slack}>here</a>.</p>,
            icon: "free",
            alignContent: true
          }]}/>
        </Section>
        <Section heading={"Cultural Currency in three steps"}>
          <Cards iconColor={"green"} cards={[
            {
              header: "Reward",
              content: "Promote the things that matter to your people and planet. and build a pride in your company",
              link: "/reward",
              icon: "reward",
            },
            {
              header: "Recognise",
              content: "Create a recognition rich work environment with free flowing feedback",
              link: routes.slack,
              icon: "recognise",
              buttonText: "Get Started",
              isOuterLink: true
            },
            {
              header: "Donate",
              content: "Donate your Cultural Currency to causes your employees care about and make your giving go further",
              link: "/donate",
              icon: "donate",
            },
          ]}/>
        </Section>
      </Container>
    </div>
  )
}


export default Recognise